// ---------------------------------------------------------------------------
// The POS cart.
//
// Every number in here is a string holding a decimal value, and every sum goes
// through Decimal. Not one line uses JavaScript arithmetic on money or weight.
// That is not fussiness: `0.1 + 0.2` is `0.30000000000000004`, and a day of
// those makes a till that does not balance.
//
// The cart holds a SNAPSHOT of each product's name, unit and price at the
// moment it was added. If the owner reprices beef while a customer is being
// served, the line already in the cart keeps the price it was quoted at. The
// same principle as storing the exchange rate on a sale.
//
// This store is browser-only working state. Nothing here is a record of
// anything until Phase 3 turns it into a sale inside a database transaction.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import Decimal from 'decimal.js';
import { toDecimal, type Amount } from '../utils/format';
import type { ComboOffer, Customer, CustomerOrder, Product, ProductPromotion, ProductVariant, PromotionType, SellingUnit } from '../types';

Decimal.set({ rounding: Decimal.ROUND_HALF_UP, precision: 28 });

/// How the quantity for a line was entered, so the cart can show it back the
/// same way the cashier typed it.
export type QuantityMode =
  /// A weight in kilograms, to three decimals.
  | 'WEIGHT_KG'
  /// A whole count of pieces, trays, boxes, meals.
  | 'COUNT'
  /// A money amount the customer asked for: "give me $10 of kafta".
  | 'CUSTOM_AMOUNT';

export interface CartLine {
  /// Line identity, not product identity. The same product can appear twice
  /// with different weights and different notes.
  id: string;
  productId: string;
  /// WHICH TYPE, when the product has types. Part of the line's identity:
  /// tawook أحمر and tawook مدخن are different things at different prices and
  /// must never merge into one line.
  variantId: string | null;

  // --- snapshot taken when the line was added ----------------------------
  nameEn: string;
  nameAr: string;
  /// The type's names as they were when the line was added, so the cart, the
  /// receipt and the butcher's ticket all say the same thing.
  variantNameEn: string | null;
  variantNameAr: string | null;
  /// And WHAT that choice was called - "الحجم" / "Size". Snapshotted with the
  /// rest of the line so a cart held today, or restored next week, still says
  /// what it said when the cashier answered the question. Null reads as a
  /// type, which is what everything chosen before options existed was.
  variantLabelEn: string | null;
  variantLabelAr: string | null;
  sku: string;
  sellingUnit: SellingUnit;
  /// Price per unit, in DOLLARS, as a decimal string. Derived; kept because
  /// the payment dialog, the reports and the sale payload still speak dollars.
  unitPrice: string;
  /// THE PRICE, in whole lira, exactly as the shop set it. Null only for a
  /// product priced before exact lira existed, whose dollars are converted at
  /// the rate instead - the same fallback the rest of the application uses.
  unitPriceLbp: string | null;
  imageUrl: string | null;

  quantityMode: QuantityMode;
  /// Kilograms for WEIGHT_KG, a whole number for COUNT, always "1" for
  /// CUSTOM_AMOUNT.
  quantity: string;
  /// Only for CUSTOM_AMOUNT: the money figure the customer asked for.
  customAmount: string | null;

  note: string | null;

  // --- the promotion this line was quoted under ----------------------------
  //
  // `unitPriceLbp` above is the EFFECTIVE price - what the server's engine
  // said this costs right now - and these say why. The till never works a
  // discount out; it carries the server's answer and shows it. A combo
  // component carries the combo's identity and its allocated line as a
  // FIGURE, because the offer price is not unit x quantity.
  //
  // Optional on the type so a line built before promotions existed (an
  // older cached cart, a test fixture) reads as an ordinary NONE line.
  basePriceLbp?: string | null;
  promotionType?: PromotionType;
  discountPercent?: string | null;
  discountAmountLbp?: string | null;
  comboOfferId?: string | null;
  comboGroupKey?: string | null;
  comboNameEn?: string | null;
  comboNameAr?: string | null;
  comboOfferPriceLbp?: string | null;
  comboRegularPriceLbp?: string | null;
  /// The line's total as a figure, for a combo component. Null otherwise.
  fixedLineTotalLbp?: string | null;
}

interface CartState {
  lines: CartLine[];
  orderNote: string;
  customer: Customer | null;
  counterFee: { amount: string; currency: 'USD' | 'LBP' };
  orderId: string | null;
  restoredFromHeldId: string | null;
  preservationId: string | null;
  handoffOrderId: string | null;
  setHandoffOrderId: (id: string | null) => void;
  setCustomer: (customer: Customer | null) => void;
  setCounterFee: (fee: CartState['counterFee'] | ((fee: CartState['counterFee']) => CartState['counterFee'])) => void;
  setRestoredFromHeldId: (id: string | null) => void;
  setPreservationId: (id: string | null) => void;
  loadOrder: (order: CustomerOrder) => void;

  addLine: (
    product: Product,
    options: {
      quantity: string;
      mode: QuantityMode;
      customAmount?: string;
      note?: string;
      /// The chosen type. Required for a product with variants - the caller
      /// (the keypad) will not let a line through without one.
      variant?: ProductVariant | null;
    },
  ) => void;
  updateLine: (
    id: string,
    patch: Partial<Pick<CartLine, 'quantity' | 'note' | 'customAmount'>> & {
      /// Switching a line from مدخن to أحمر: the names and the unit price all
      /// move with it, because they are what the line IS.
      variant?: ProductVariant | null;
    },
  ) => void;
  removeLine: (id: string) => void;
  /// Put a whole combo on the till: one line per component, tied together
  /// by a fresh group key, priced by the server's allocation.
  addCombo: (combo: ComboOffer) => void;
  /// Take a whole combo off: every line of the group goes together.
  removeComboGroup: (groupKey: string) => void;
  clear: () => void;
  setOrderNote: (note: string) => void;
  /// Put a whole cart on the till at once, replacing whatever was there.
  ///
  /// For carts that come back from somewhere - a held cart, or an unpaid
  /// delivery order reopened to take payment. Deliberately NOT built out of
  /// repeated `addLine` calls: those merge counted lines and would quietly
  /// turn two separate orders of the same thing into one.
  replaceLines: (lines: CartLine[], orderNote?: string) => void;
}

/// Build a cart line from something that is not a product in the catalogue -
/// a held item or an order item, both of which carry their own snapshot.
export function cartLineFrom(source: {
  productId: string;
  variantId?: string | null;
  nameEn: string;
  nameAr: string;
  variantNameEn?: string | null;
  variantNameAr?: string | null;
  variantLabelEn?: string | null;
  variantLabelAr?: string | null;
  sku: string;
  sellingUnit: SellingUnit;
  unitPrice: string;
  unitPriceLbp?: string | null;
  quantityMode: QuantityMode;
  quantity: string;
  customAmount?: string | null;
  note?: string | null;
  basePriceLbp?: string | null;
  promotionType?: PromotionType;
  discountPercent?: string | null;
  comboOfferId?: string | null;
  comboGroupKey?: string | null;
  comboNameEn?: string | null;
  comboNameAr?: string | null;
  comboOfferPriceLbp?: string | null;
  comboRegularPriceLbp?: string | null;
  lineTotalLbp?: string | null;
}): CartLine {
  return {
    id: newLineId(),
    productId: source.productId,
    variantId: source.variantId ?? null,
    nameEn: source.nameEn,
    nameAr: source.nameAr,
    variantNameEn: source.variantNameEn ?? null,
    variantNameAr: source.variantNameAr ?? null,
    variantLabelEn: source.variantLabelEn ?? null,
    variantLabelAr: source.variantLabelAr ?? null,
    unitPriceLbp: source.unitPriceLbp ?? null,
    sku: source.sku,
    sellingUnit: source.sellingUnit,
    unitPrice: source.unitPrice,
    imageUrl: null,
    quantityMode: source.quantityMode,
    quantity: source.quantity,
    customAmount: source.quantityMode === 'CUSTOM_AMOUNT' ? (source.customAmount ?? '0') : null,
    note: source.note?.trim() ? source.note.trim() : null,
    basePriceLbp: source.basePriceLbp ?? null,
    promotionType: source.promotionType ?? 'NONE',
    discountPercent: source.discountPercent ?? null,
    comboOfferId: source.comboOfferId ?? null,
    comboGroupKey: source.comboGroupKey ?? null,
    comboNameEn: source.comboNameEn ?? null,
    comboNameAr: source.comboNameAr ?? null,
    comboOfferPriceLbp: source.comboOfferPriceLbp ?? null,
    comboRegularPriceLbp: source.comboRegularPriceLbp ?? null,
    // A combo component's line is a figure, not unit x quantity.
    fixedLineTotalLbp: source.comboGroupKey ? (source.lineTotalLbp ?? null) : null,
  };
}

/// The combo groups in a cart, in first-appearance order, each with its lines.
export function comboGroupsOf(lines: CartLine[]): Array<{ key: string; lines: CartLine[] }> {
  const groups = new Map<string, CartLine[]>();
  for (const line of lines) {
    if (!line.comboGroupKey) continue;
    const group = groups.get(line.comboGroupKey);
    if (group) group.push(line);
    else groups.set(line.comboGroupKey, [line]);
  }
  return [...groups.entries()].map(([key, groupLines]) => ({ key, lines: groupLines }));
}

/// Which mode a product is entered in by default.
export function defaultModeFor(unit: SellingUnit): QuantityMode {
  if (unit === 'KG' || unit === 'GRAM') return 'WEIGHT_KG';
  if (unit === 'CUSTOM_AMOUNT') return 'CUSTOM_AMOUNT';
  return 'COUNT';
}

/// True when this product is sold by weight, so the keypad should offer
/// kilograms and grams.
export const isWeighed = (unit: SellingUnit): boolean => unit === 'KG' || unit === 'GRAM';

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/// What one line costs. Rounded to cents once, at the end - never per step.
export function lineTotal(line: CartLine, rate?: Amount | null): Decimal {
  if (line.quantityMode === 'CUSTOM_AMOUNT') {
    // "Ten dollars of this" starts life in dollars; the server converts it to
    // lira once and derives the same dollars back - a round trip that is
    // exact at any realistic rate, because a cent is thousands of lira.
    return toDecimal(line.customAmount ?? '0').toDecimalPlaces(2);
  }

  // THE SETTLEMENT CONTRACT, and the fix for the phantom $0.01.
  //
  // Dollars are DERIVED from the exact lira line - one division, one
  // rounding, per line - because that is precisely how the server works the
  // sale out (`roundUsd(lineTotalLbp / rate)`). This used to multiply the
  // product's already-rounded dollar unit price by the quantity instead,
  // which drifts: a unit price shown as 15.64 is really 15.6424..., and
  // 15.64 x 2.05 lands a cent under what the exact lira line divides to.
  // The till then displayed a total one cent below what the server would
  // demand, the cashier paid the displayed figure exactly, and the sale was
  // refused with "$0.01 is still owed" - over money nobody actually owed.
  //
  // With both sides deriving from the same lira figure by the same rule,
  // they agree cent for cent BY CONSTRUCTION. No tolerance anywhere: a
  // payment one real cent short is still one real cent short.
  const usdToLbp = rate === null || rate === undefined ? null : toDecimal(rate);
  if (usdToLbp && usdToLbp.greaterThan(0)) {
    return lineTotalLbp(line, usdToLbp).dividedBy(usdToLbp).toDecimalPlaces(2);
  }

  // No rate loaded (a till mid-bootstrap): the old arithmetic, which at
  // least prices the line, until the rate arrives and the exact path takes
  // over.
  return toDecimal(line.unitPrice).times(toDecimal(line.quantity)).toDecimalPlaces(2);
}

/// The sum of every line, in dollars - each line derived from its lira
/// figure exactly as `lineTotal` describes, then added, which is the same
/// order of operations the server uses for its own subtotal.
export function cartSubtotal(lines: CartLine[], rate?: Amount | null): Decimal {
  return lines
    .reduce((sum, line) => sum.plus(lineTotal(line, rate)), new Decimal(0))
    .toDecimalPlaces(2);
}

// ---------------------------------------------------------------------------
// The same two sums, in the money the shop actually deals in.
//
// These are the AUTHORITATIVE ones, and the server works the sale out the same
// way from its own rows. The dollar pair above is what the till shows in small
// type beside them and what the payment dialog reconciles against.
//
// The rate is only ever used to convert a line that has no exact lira price -
// a product nobody has re-priced since exact lira existed. Nothing here
// multiplies a rounded dollar figure back into lira, which was the bug.
// ---------------------------------------------------------------------------

/// What ONE of something costs, in whole lira.
///
/// THE SINGLE RULE, and every screen that shows or multiplies a price uses it:
/// the weight modal, the product card, the cart. The server mirrors it exactly
/// in `exactLbpPrice`.
///
/// Two cases:
///
///   the shop set an exact lira price - use it, untouched;
///
///   it did not, and all there is is the old dollar column - convert it and
///   round to whole lira HERE, once, before anything is multiplied by it.
///
/// The rounding position is the whole point. The weight modal used to round
/// the DOLLARS to cents and convert that, while the line beside it converted
/// first and rounded the lira - so one kilo of kafta was displayed at 1,400,004
/// and totalled at 1,399,780 on the same screen. Two rounding orders on one
/// price is two prices.
export function unitPriceInLbp(
  unitPriceLbp: string | null | undefined,
  unitPriceUsd: Amount,
  rate: Amount,
): Decimal {
  if (unitPriceLbp) return toDecimal(unitPriceLbp);
  return toDecimal(unitPriceUsd).times(toDecimal(rate)).toDecimalPlaces(0);
}

/// THE ONE QUOTE for what one of a product - or one of its options - costs
/// right now: the figure the card shows, the weight panel previews and the
/// cart line carries.
///
/// It used to be decided in three places. The card and the cart both looked
/// for the server's promotion block; the weight panel read the base price
/// off the product and never looked. So a discounted kilo of beef cubes read
/// 960,000 on the shelf, 1,200,000 in «أدخل الوزن», and 960,000 again in the
/// order - one product, two prices on one screen. Now there is one rule,
/// here, and everything that quotes a price reads it.
///
/// The EFFECTIVE lira when the server sent a promotion block (the base
/// through the product's or the shelf's discount, worked out by the one
/// engine); the base otherwise, exactly as before promotions existed. The
/// dollars are the product's own dollar column, kept for legacy products
/// with no lira price; every derived dollar figure comes from the lira.
export interface UnitQuote {
  promotion: ProductPromotion | null;
  /// Whole lira, or null for a legacy product priced only in dollars.
  unitPriceLbp: string | null;
  unitPriceUsd: string;
  /// The base the quote came from, when a promotion applied.
  basePriceLbp: string | null;
}

export function quotedUnitPrice(
  product: Pick<Product, 'sellingPrice' | 'sellingPriceLbp' | 'promotion'>,
  variant: ProductVariant | null,
): UnitQuote {
  // The option's own block when an option was chosen, the product's otherwise.
  const promotion = variant ? (variant.promotion ?? null) : (product.promotion ?? null);
  return {
    promotion,
    unitPriceLbp: promotion
      ? promotion.effectivePriceLbp
      : variant
        ? (variant.sellingPriceLbp ?? null)
        : (product.sellingPriceLbp ?? null),
    unitPriceUsd: variant ? variant.sellingPrice : (product.sellingPrice ?? '0'),
    basePriceLbp: promotion ? promotion.basePriceLbp : null,
  };
}

/// What one line comes to, in whole lira.
export function lineTotalLbp(line: CartLine, rate: Amount): Decimal {
  // A combo component: its allocated share of the offer, as the server
  // worked it out. The shares add up to the offer price exactly; a unit
  // price multiplied back would not.
  if (line.fixedLineTotalLbp) return toDecimal(line.fixedLineTotalLbp);

  const perUnit = unitPriceInLbp(line.unitPriceLbp, line.unitPrice, rate);

  if (line.quantityMode === 'CUSTOM_AMOUNT') {
    // "Give me ten dollars of this": the money is the input, so it is the one
    // figure that genuinely starts life in dollars.
    return toDecimal(line.customAmount ?? '0').times(toDecimal(rate)).toDecimalPlaces(0);
  }

  // ONE rounding boundary, at the end: 950,000 a kilo of 1.2 kg is 1,140,000.
  return perUnit.times(toDecimal(line.quantity)).toDecimalPlaces(0);
}

/// The sum of every line, in whole lira.
export function cartSubtotalLbp(lines: CartLine[], rate: Amount): Decimal {
  return lines.reduce((sum, line) => sum.plus(lineTotalLbp(line, rate)), new Decimal(0));
}

/// Would this cart send anything to a preparation bench?
///
/// VISIBILITY, not routing. The server alone decides where paper goes; this
/// answers the much smaller question "is the بدون تحضير checkbox worth
/// showing?" - and it is the ONE sanctioned place the till may look at a
/// category's kitchen routing, which the kitchenRouting guard test enforces.
/// The benchless-but-kitchen-routed legacy category counts as preparation
/// here on purpose: that is exactly the cart whose NORMAL sale the server
/// refuses, and hiding the skip option from it would leave the cashier no
/// way out.
export function cartNeedsPreparation(
  lines: CartLine[],
  products: Array<{ id: string; category?: { id: string } | null }>,
  categories: Array<{ id: string; printRouting?: string | null }> | undefined,
): boolean {
  return lines.some((line) => {
    const product = products.find((row) => row.id === line.productId);
    const categoryId = product?.category?.id;
    if (!categoryId) return false;
    const category = categories?.find((row) => row.id === categoryId);
    return category?.printRouting === 'KITCHEN_AND_CASHIER';
  });
}

/// Total weight of the weighed lines, for the receipt summary.
export function cartTotalWeightKg(lines: CartLine[]): Decimal {
  return lines
    .filter((line) => line.quantityMode === 'WEIGHT_KG')
    .reduce((sum, line) => sum.plus(toDecimal(line.quantity)), new Decimal(0))
    .toDecimalPlaces(3);
}

/// How many individual things are in the cart, for the "3 items" badge.
export function cartItemCount(lines: CartLine[]): number {
  return lines.length;
}

// ---------------------------------------------------------------------------
// Weight entry
// ---------------------------------------------------------------------------

/// Grams to kilograms: 1250 g becomes "1.250".
///
/// Done with Decimal rather than `grams / 1000`, because dividing a float by a
/// thousand is exactly where precision quietly disappears.
export function gramsToKg(grams: string | number): string {
  return toDecimal(grams).dividedBy(1000).toDecimalPlaces(3).toFixed(3);
}

export function kgToGrams(kg: string | number): string {
  return toDecimal(kg).times(1000).toDecimalPlaces(0).toFixed(0);
}

/// Turn what the cashier typed into the kilogram figure the cart stores.
export function normaliseWeightEntry(raw: string, entryUnit: 'kg' | 'g'): string {
  const value = toDecimal(raw);
  return entryUnit === 'g'
    ? value.dividedBy(1000).toDecimalPlaces(3).toFixed(3)
    : value.toDecimalPlaces(3).toFixed(3);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface QuantityCheck {
  ok: boolean;
  /// A message the cashier can act on, or null when the entry is fine.
  problem: string | null;
}

/// Checked here as well as on the server. The server is the authority; this is
/// so the cashier is told immediately rather than after a round trip.
export function checkQuantity(raw: string, mode: QuantityMode): QuantityCheck {
  const trimmed = raw.trim();

  if (trimmed.length === 0) {
    return { ok: false, problem: 'EMPTY' };
  }

  if (!/^\d*\.?\d*$/.test(trimmed) || trimmed === '.') {
    return { ok: false, problem: 'NOT_A_NUMBER' };
  }

  const value = toDecimal(trimmed);

  if (value.lessThanOrEqualTo(0)) {
    return { ok: false, problem: 'ZERO_OR_LESS' };
  }

  if (mode === 'COUNT' && !value.isInteger()) {
    return { ok: false, problem: 'MUST_BE_WHOLE' };
  }

  // A guard against a stuck keypad or a mis-typed weight - 10,000 kg of kafta
  // is not a sale, it is a mistake.
  if (value.greaterThan(10000)) {
    return { ok: false, problem: 'TOO_LARGE' };
  }

  return { ok: true, problem: null };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

function newLineId(): string {
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useCartStore = create<CartState>()(persist((set) => ({
  lines: [],
  orderNote: '',
  customer: null,
  counterFee: { amount: '', currency: 'LBP' },
  orderId: null,
  restoredFromHeldId: null,
  preservationId: null,
  handoffOrderId: null,
  setHandoffOrderId: (handoffOrderId) => set({ handoffOrderId }),
  setCustomer: (customer) => set({ customer }),
  setCounterFee: (fee) => set(state => ({ counterFee: typeof fee === 'function' ? fee(state.counterFee) : fee })),
  setRestoredFromHeldId: (restoredFromHeldId) => set({ restoredFromHeldId }),
  setPreservationId: (preservationId) => set({ preservationId }),
  loadOrder: (order) => set({
    lines: order.items.map(item => ({
      ...cartLineFrom({
        ...item,
        productId: item.product.id,
        nameEn: item.product.nameEn,
        nameAr: item.product.nameAr,
        sku: item.product.sku,
        sellingUnit: item.product.sellingUnit,
        unitPrice: item.unitPriceUsd,
        quantityMode: defaultModeFor(item.product.sellingUnit) === 'CUSTOM_AMOUNT' ? 'COUNT' : defaultModeFor(item.product.sellingUnit),
        note: item.preparationNotes,
      }),
      // The operational DTO already overlays the historical MO names/SKU/unit.
      // Every frozen line total is authoritative, including allocation rounding.
      fixedLineTotalLbp: order.onlineOrder ? item.lineTotalLbp : (item.comboGroupKey ? item.lineTotalLbp : null),
      discountAmountLbp: item.discountAmountLbp,
    })),
    orderNote: order.onlineOrder ? (order.note ?? order.onlineOrder.customerNotes ?? '') : (order.preparationNotes ?? ''),
    orderId: order.id,
    handoffOrderId: null,
    customer: null,
    counterFee: { amount: '', currency: 'LBP' },
    restoredFromHeldId: null,
    preservationId: null,
  }),

  addLine: (product, options) =>
    set((state) => {
      const variant = options.variant ?? null;
      // THE ONE QUOTE - the same figure the card showed and the panel previewed.
      const quote = quotedUnitPrice(product, variant);
      const promotion = quote.promotion;

      const line: CartLine = {
        id: newLineId(),
        productId: product.id,
        variantId: variant?.id ?? null,
        nameEn: product.nameEn,
        nameAr: product.nameAr,
        variantNameEn: variant?.nameEn ?? null,
        variantNameAr: variant?.nameAr ?? null,
        // Read off the PRODUCT at the moment of adding, exactly as the sale
        // will read it at the moment of selling.
        variantLabelEn: variant ? (product.optionLabelEn ?? null) : null,
        variantLabelAr: variant ? (product.optionLabelAr ?? null) : null,
        sku: product.sku,
        sellingUnit: product.sellingUnit,
        // THE CHOSEN OPTION'S PRICE when there is one. The parent's price is
        // not the price of anything for a product with options - and for such
        // a product it is now null, so there is nothing to fall back to.
        unitPrice: quote.unitPriceUsd,
        // ...and the same price in the money the owner set it in. This is what
        // the line is worked out from; the dollars above are what it comes to.
        //
        // THE EFFECTIVE PRICE when the server sent one: the base through the
        // product's or the shelf's discount, worked out by the one engine
        // and carried here as a figure. Without a promotion block (no rate
        // yet, an older cached catalogue) the base is the price, as before.
        unitPriceLbp: quote.unitPriceLbp,
        imageUrl: product.imageUrl,
        quantityMode: options.mode,
        quantity: options.mode === 'CUSTOM_AMOUNT' ? '1' : options.quantity,
        customAmount: options.mode === 'CUSTOM_AMOUNT' ? (options.customAmount ?? '0') : null,
        note: options.note?.trim() ? options.note.trim() : null,
        basePriceLbp: promotion ? promotion.basePriceLbp : null,
        promotionType: promotion ? promotion.promotionType : 'NONE',
        discountPercent: promotion?.discountPercent ?? null,
        comboOfferId: null,
        comboGroupKey: null,
        comboNameEn: null,
        comboNameAr: null,
        comboOfferPriceLbp: null,
        comboRegularPriceLbp: null,
        fixedLineTotalLbp: null,
      };

      // Counted items of the same product with the same note are merged, which
      // is what a cashier expects when they tap "chicken burger" four times.
      // Weighed items never merge: 1.250 kg and 0.800 kg are two separate
      // pieces of meat, and combining them would hide that from the customer.
      if (line.quantityMode === 'COUNT') {
        const existingIndex = state.lines.findIndex(
          (candidate) =>
            candidate.productId === line.productId &&
            // The TYPE is part of what a line is. Two burgers are one line of
            // two; a spicy burger and a classic one are two lines, and
            // merging them would send the wrong thing to the kitchen.
            candidate.variantId === line.variantId &&
            candidate.quantityMode === 'COUNT' &&
            candidate.note === line.note &&
            candidate.unitPrice === line.unitPrice &&
            // A combo's component is the combo's, never a place to pile a
            // separately-priced tap of the same product.
            !candidate.comboGroupKey &&
            (candidate.promotionType ?? 'NONE') === (line.promotionType ?? 'NONE'),
        );

        if (existingIndex >= 0) {
          const existing = state.lines[existingIndex]!;
          const merged: CartLine = {
            ...existing,
            quantity: toDecimal(existing.quantity).plus(toDecimal(line.quantity)).toFixed(0),
          };
          const next = [...state.lines];
          next[existingIndex] = merged;
          return { lines: next };
        }
      }

      return { lines: [...state.lines, line] };
    }),

  updateLine: (id, patch) =>
    set((state) => ({
      lines: state.lines.map((line) =>
        line.id === id
          ? {
              ...line,
              ...(patch.quantity !== undefined ? { quantity: patch.quantity } : {}),
              ...(patch.customAmount !== undefined ? { customAmount: patch.customAmount } : {}),
              ...(patch.note !== undefined ? { note: patch.note?.trim() ? patch.note.trim() : null } : {}),
              // Changing the type on a line that has not been paid for takes
              // its price with it. An unsold line is a quote, not a record.
              ...(patch.variant
                ? {
                    variantId: patch.variant.id,
                    variantNameEn: patch.variant.nameEn,
                    variantNameAr: patch.variant.nameAr,
                    unitPrice: patch.variant.sellingPrice,
                    // The new option's EFFECTIVE price, through the same rule.
                    unitPriceLbp: quotedUnitPrice({ sellingPrice: null, sellingPriceLbp: null, promotion: null }, patch.variant).unitPriceLbp,
                    basePriceLbp: patch.variant.promotion?.basePriceLbp ?? null,
                    promotionType: patch.variant.promotion?.promotionType ?? 'NONE',
                    discountPercent: patch.variant.promotion?.discountPercent ?? null,
                  }
                : {}),
            }
          : line,
      ),
    })),

  removeLine: (id) =>
    set((state) => {
      // Removing a combo component removes the combo: half a bundle at the
      // bundle's price is not a thing the shop sells.
      const target = state.lines.find((line) => line.id === id);
      if (target?.comboGroupKey) {
        return { lines: state.lines.filter((line) => line.comboGroupKey !== target.comboGroupKey) };
      }
      return { lines: state.lines.filter((line) => line.id !== id) };
    }),

  addCombo: (combo) =>
    set((state) => {
      // A fresh group per tap: two of the same combo are two groups, each
      // priced whole. The server re-validates the group against the combo
      // as it stands and prices it from the offer - these lines carry its
      // allocation for the screen, never a price of the till's own.
      const groupKey = `combo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const lines: CartLine[] = combo.components.map((component) => ({
        id: newLineId(),
        productId: component.productId,
        variantId: component.variantId,
        nameEn: component.nameEn,
        nameAr: component.nameAr,
        variantNameEn: component.variantNameEn,
        variantNameAr: component.variantNameAr,
        variantLabelEn: component.variantLabelEn,
        variantLabelAr: component.variantLabelAr,
        sku: component.sku,
        sellingUnit: component.sellingUnit,
        // The allocated share divided back, for the dollar column the
        // payment dialog reads; the lira line itself is the fixed figure.
        unitPrice: '0',
        unitPriceLbp: toDecimal(component.quantity).greaterThan(0)
          ? toDecimal(component.allocatedLineLbp).dividedBy(toDecimal(component.quantity)).toDecimalPlaces(0).toFixed(0)
          : '0',
        imageUrl: component.imageUrl,
        quantityMode: isWeighed(component.sellingUnit) ? 'WEIGHT_KG' : 'COUNT',
        quantity: component.quantity,
        customAmount: null,
        note: null,
        basePriceLbp: component.baseUnitPriceLbp,
        promotionType: 'COMBO_OFFER',
        discountPercent: null,
        comboOfferId: combo.id,
        comboGroupKey: groupKey,
        comboNameEn: combo.nameEn,
        comboNameAr: combo.nameAr,
        comboOfferPriceLbp: combo.offerPriceLbp,
        comboRegularPriceLbp: combo.regularTotalLbp,
        fixedLineTotalLbp: component.allocatedLineLbp,
      }));
      return { lines: [...state.lines, ...lines] };
    }),

  removeComboGroup: (groupKey) =>
    set((state) => ({ lines: state.lines.filter((line) => line.comboGroupKey !== groupKey) })),

  clear: () => set({ lines: [], orderNote: '', customer: null, counterFee: { amount: '', currency: 'LBP' }, orderId: null, restoredFromHeldId: null, preservationId: null, handoffOrderId: null }),

  setOrderNote: (note) => set({ orderNote: note }),

  replaceLines: (lines, orderNote) => set({ lines, orderNote: orderNote ?? '' }),
}), {
  name: 'yaghi-pos.current-sale',
  storage: createJSONStorage(() => sessionStorage),
  partialize: ({ lines, orderNote, customer, counterFee, orderId, restoredFromHeldId, preservationId, handoffOrderId }) => ({ lines, orderNote, customer, counterFee, orderId, restoredFromHeldId, preservationId, handoffOrderId }),
}));
